"use client";

import type { ButtonHTMLAttributes } from "react";
import { DayPicker } from "react-day-picker";
import type { Modifiers } from "react-day-picker";
import "react-day-picker/dist/style.css";

export interface CalendarPickerProps extends Record<string, any> {
  /** When provided, a localised "Today" label is shown below the day number on today's cell */
  todayLabel?: string;
}

/**
 * Custom DayButton component factory.
 * Returns a DayButton that, when `modifiers.today` is true, renders a tiny
 * "today" badge beneath the day number so the user can instantly spot the
 * current date in the full-month view.
 */
function makeTodayDayButton(todayLabel: string) {
  return function TodayDayButton({
    day,
    modifiers,
    children,
    ...buttonProps
  }: { day: any; modifiers: Modifiers } & ButtonHTMLAttributes<HTMLButtonElement>) {
    if (!modifiers.today) {
      return <button {...buttonProps}>{children}</button>;
    }

    // Wrap the day number + label in a flex column so the button keeps its
    // original sizing but gains the label underneath.
    return (
      <button
        {...buttonProps}
        style={{
          ...((buttonProps as any).style ?? {}),
          height: "auto",
          width: "auto",
          minWidth: "2.5rem",
        }}
      >
        <span className="flex flex-col items-center leading-none gap-1">
          <span>{children}</span>
          <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full bg-warning text-on-warning whitespace-nowrap">
            {todayLabel}
          </span>
        </span>
      </button>
    );
  };
}

export default function CalendarPicker({
  className = "",
  classNames,
  mode = "single",
  todayLabel,
  components: componentsProp,
  ...props
}: CalendarPickerProps) {
  const defaultClassNames = {
    months: "flex w-full justify-center",
    month: "w-full space-y-3 sm:space-y-5",
    month_caption: "relative flex h-10 sm:h-12 items-center justify-center mb-2",
    caption_label: "text-base sm:text-lg font-bold text-foreground",
    nav: "pointer-events-none absolute left-0 right-0 top-0 z-10 flex h-10 sm:h-11 items-center justify-between",
    button_previous: "pointer-events-auto h-8 w-8 sm:h-9 sm:w-9 mt-2 sm:mt-5 ms-2 sm:ms-5 rounded-full border border-border hover:bg-hover transition-colors duration-150 rtl:rotate-180",
    button_next: "pointer-events-auto h-8 w-8 sm:h-9 sm:w-9 mt-2 sm:mt-5 me-2 sm:me-5 rounded-full border border-border hover:bg-hover transition-colors duration-150 rtl:rotate-180",
    month_grid: "w-full table-fixed border-separate border-spacing-x-2 sm:border-spacing-x-2 md:border-spacing-x-4 border-spacing-y-4 sm:border-spacing-y-4",
    weekday: "text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-text-muted",
    day: "text-center align-middle",
    day_button: "mx-auto flex h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 items-center justify-center text-xs sm:text-sm font-medium transition-colors duration-150 cursor-pointer",
    today: "rounded-full border border-secondary text-foreground",
    selected: "!bg-success !text-on-success rounded-full",
    disabled: "!cursor-not-allowed line-through text-text-muted opacity-40",
    ...classNames,
  };

  // Merge caller-supplied components with our today-label override (if any)
  const mergedComponents = todayLabel
    ? { DayButton: makeTodayDayButton(todayLabel), ...componentsProp }
    : componentsProp;

  return (
    <DayPicker
      mode={mode}
      className={`relative mx-auto w-full max-w-2xl rounded-[10px] border border-border bg-surface-elevated p-4 md:p-6 ${className}`}
      classNames={defaultClassNames}
      components={mergedComponents}
      {...props}
    />
  );
}
